/**
 * Surface IDF Synchronization
 *
 * Creates and updates BuildingSurface:Detailed IDF objects
 * from geometry editing operations.
 */

import type { IDFObject, IDFField } from '../../parser/types';
import type { Vector3, BoundaryCondition, SurfaceType } from '../types';
import {
  SURFACE_FIELD_MAPPINGS,
  surfaceTypeToIDF,
  boundaryConditionToIDF,
  getSunExposure,
  getWindExposure,
} from './fieldMappings';

/**
 * Result of creating a surface IDF object.
 */
export interface SurfaceIDFResult {
  /** The created IDF object */
  surface: Omit<IDFObject, 'id'>;
}

/**
 * Create a field object for IDF.
 */
function createField(value: string, comment?: string): IDFField {
  return {
    value,
    ...(comment && { inlineComment: `!- ${comment}` }),
  };
}

/**
 * Create a new BuildingSurface:Detailed IDF object.
 */
export function createSurfaceIDFObject(
  name: string,
  surfaceType: SurfaceType,
  construction: string,
  zoneName: string,
  boundaryCondition: BoundaryCondition,
  outsideBoundaryObject: string,
  vertices: Vector3[]
): SurfaceIDFResult {
  const bcString = boundaryConditionToIDF(boundaryCondition);

  const fields: IDFField[] = [
    createField(name, 'Name'),
    createField(surfaceTypeToIDF(surfaceType), 'Surface Type'),
    createField(construction, 'Construction Name'),
    createField(zoneName, 'Zone Name'),
    createField('', 'Space Name'),
    createField(bcString, 'Outside Boundary Condition'),
    createField(outsideBoundaryObject || '', 'Outside Boundary Condition Object'),
    createField(getSunExposure(boundaryCondition), 'Sun Exposure'),
    createField(getWindExposure(boundaryCondition), 'Wind Exposure'),
    createField('autocalculate', 'View Factor to Ground'),
    createField(String(vertices.length), 'Number of Vertices'),
  ];

  // Add vertices
  vertices.forEach((v, i) => {
    fields.push(createField(String(v.x), `Vertex ${i + 1} X-coordinate`));
    fields.push(createField(String(v.y), `Vertex ${i + 1} Y-coordinate`));
    fields.push(createField(String(v.z), `Vertex ${i + 1} Z-coordinate`));
  });

  return {
    surface: {
      className: 'BuildingSurface:Detailed',
      fields,
      leadingComments: [],
    },
  };
}

/**
 * Update a surface IDF object.
 */
export function updateSurfaceIDFObject(
  surfaceObject: IDFObject,
  updates: {
    name?: string;
    surfaceType?: SurfaceType;
    construction?: string;
    zoneName?: string;
    boundaryCondition?: BoundaryCondition;
    outsideBoundaryObject?: string;
    vertices?: Vector3[];
  }
): IDFObject {
  const fields = [...surfaceObject.fields];

  if (updates.name !== undefined) {
    if (fields[SURFACE_FIELD_MAPPINGS.NAME.index]) {
      fields[SURFACE_FIELD_MAPPINGS.NAME.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.NAME.index],
        value: updates.name,
      };
    }
  }

  if (updates.surfaceType !== undefined) {
    if (fields[SURFACE_FIELD_MAPPINGS.SURFACE_TYPE.index]) {
      fields[SURFACE_FIELD_MAPPINGS.SURFACE_TYPE.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.SURFACE_TYPE.index],
        value: surfaceTypeToIDF(updates.surfaceType),
      };
    }
  }

  if (updates.construction !== undefined) {
    if (fields[SURFACE_FIELD_MAPPINGS.CONSTRUCTION_NAME.index]) {
      fields[SURFACE_FIELD_MAPPINGS.CONSTRUCTION_NAME.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.CONSTRUCTION_NAME.index],
        value: updates.construction,
      };
    }
  }

  if (updates.zoneName !== undefined) {
    if (fields[SURFACE_FIELD_MAPPINGS.ZONE_NAME.index]) {
      fields[SURFACE_FIELD_MAPPINGS.ZONE_NAME.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.ZONE_NAME.index],
        value: updates.zoneName,
      };
    }
  }

  if (updates.boundaryCondition !== undefined) {
    const bcString = boundaryConditionToIDF(updates.boundaryCondition);
    if (fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION.index]) {
      fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION.index],
        value: bcString,
      };
    }
    // Update sun and wind exposure based on boundary condition
    if (fields[SURFACE_FIELD_MAPPINGS.SUN_EXPOSURE.index]) {
      fields[SURFACE_FIELD_MAPPINGS.SUN_EXPOSURE.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.SUN_EXPOSURE.index],
        value: getSunExposure(updates.boundaryCondition),
      };
    }
    if (fields[SURFACE_FIELD_MAPPINGS.WIND_EXPOSURE.index]) {
      fields[SURFACE_FIELD_MAPPINGS.WIND_EXPOSURE.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.WIND_EXPOSURE.index],
        value: getWindExposure(updates.boundaryCondition),
      };
    }
  }

  if (updates.outsideBoundaryObject !== undefined) {
    if (fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION_OBJECT.index]) {
      fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION_OBJECT.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION_OBJECT.index],
        value: updates.outsideBoundaryObject,
      };
    }
  }

  if (updates.vertices !== undefined) {
    // Update number of vertices
    if (fields[SURFACE_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index]) {
      fields[SURFACE_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index] = {
        ...fields[SURFACE_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index],
        value: String(updates.vertices.length),
      };
    }

    // Remove old vertices (everything after index 10)
    fields.splice(SURFACE_FIELD_MAPPINGS.VERTICES_START.index);

    // Add new vertices
    updates.vertices.forEach((v, i) => {
      fields.push(createField(String(v.x), `Vertex ${i + 1} X-coordinate`));
      fields.push(createField(String(v.y), `Vertex ${i + 1} Y-coordinate`));
      fields.push(createField(String(v.z), `Vertex ${i + 1} Z-coordinate`));
    });
  }

  return { ...surfaceObject, fields };
}

/**
 * Apply a move transformation to surface vertices.
 */
export function moveSurfaceVertices(
  surfaceObject: IDFObject,
  translation: Vector3
): IDFObject {
  const vertices = getSurfaceVertices(surfaceObject);
  const movedVertices = vertices.map((v) => ({
    x: v.x + translation.x,
    y: v.y + translation.y,
    z: v.z + translation.z,
  }));

  return updateSurfaceIDFObject(surfaceObject, { vertices: movedVertices });
}

/**
 * Flip surface normal by reversing vertex order.
 */
export function flipSurfaceNormal(surfaceObject: IDFObject): IDFObject {
  const vertices = getSurfaceVertices(surfaceObject);
  const flippedVertices = [...vertices].reverse();

  return updateSurfaceIDFObject(surfaceObject, { vertices: flippedVertices });
}

/**
 * Get vertices from a surface IDF object.
 */
export function getSurfaceVertices(surfaceObject: IDFObject): Vector3[] {
  const vertices: Vector3[] = [];
  const numVerticesField = surfaceObject.fields[SURFACE_FIELD_MAPPINGS.NUMBER_OF_VERTICES.index];
  const numVertices = numVerticesField ? parseInt(numVerticesField.value) : 0;

  for (let i = 0; i < numVertices; i++) {
    const baseIndex = SURFACE_FIELD_MAPPINGS.VERTICES_START.index + i * 3;
    const xField = surfaceObject.fields[baseIndex];
    const yField = surfaceObject.fields[baseIndex + 1];
    const zField = surfaceObject.fields[baseIndex + 2];

    if (xField && yField && zField) {
      vertices.push({
        x: parseFloat(xField.value) || 0,
        y: parseFloat(yField.value) || 0,
        z: parseFloat(zField.value) || 0,
      });
    }
  }

  return vertices;
}

/**
 * Get surface name from IDF object.
 */
export function getSurfaceName(surfaceObject: IDFObject): string {
  return surfaceObject.fields[SURFACE_FIELD_MAPPINGS.NAME.index]?.value ?? '';
}

/**
 * Get surface zone name from IDF object.
 */
export function getSurfaceZoneName(surfaceObject: IDFObject): string {
  return surfaceObject.fields[SURFACE_FIELD_MAPPINGS.ZONE_NAME.index]?.value ?? '';
}

/**
 * Get surface construction from IDF object.
 */
export function getSurfaceConstruction(surfaceObject: IDFObject): string {
  return surfaceObject.fields[SURFACE_FIELD_MAPPINGS.CONSTRUCTION_NAME.index]?.value ?? '';
}

/**
 * Get surface boundary condition from IDF object.
 */
export function getSurfaceBoundaryCondition(surfaceObject: IDFObject): string {
  return surfaceObject.fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION.index]?.value ?? '';
}

/**
 * Get outside boundary condition object from IDF object.
 */
export function getSurfaceBoundaryObject(surfaceObject: IDFObject): string {
  return surfaceObject.fields[SURFACE_FIELD_MAPPINGS.OUTSIDE_BOUNDARY_CONDITION_OBJECT.index]?.value ?? '';
}

/**
 * Link two surfaces as adjacent (update boundary conditions).
 */
export function linkAdjacentSurfaces(
  surface1: IDFObject,
  surface2: IDFObject
): { surface1: IDFObject; surface2: IDFObject } {
  const name1 = getSurfaceName(surface1);
  const name2 = getSurfaceName(surface2);

  const updatedSurface1 = updateSurfaceIDFObject(surface1, {
    boundaryCondition: 'surface',
    outsideBoundaryObject: name2,
  });

  const updatedSurface2 = updateSurfaceIDFObject(surface2, {
    boundaryCondition: 'surface',
    outsideBoundaryObject: name1,
  });

  return { surface1: updatedSurface1, surface2: updatedSurface2 };
}

/**
 * Unlink two adjacent surfaces (set to exterior).
 */
export function unlinkAdjacentSurfaces(
  surface1: IDFObject,
  surface2: IDFObject
): { surface1: IDFObject; surface2: IDFObject } {
  const updatedSurface1 = updateSurfaceIDFObject(surface1, {
    boundaryCondition: 'outdoors',
    outsideBoundaryObject: '',
  });

  const updatedSurface2 = updateSurfaceIDFObject(surface2, {
    boundaryCondition: 'outdoors',
    outsideBoundaryObject: '',
  });

  return { surface1: updatedSurface1, surface2: updatedSurface2 };
}
